/**
 * A single tier in an item's "brand waterfall": an ordered list of preferred
 * brands/models used when restocking. Ordering runs top-down — position 1 is the
 * first choice, and the next tier is only used when the one above is unavailable.
 *
 * `link` points at the exact Walmart Business product so orders can be placed
 * accurately, and `amount` is the pack size ordered (units per pack). This mirrors
 * the concierge product-defaults waterfall.
 */
export interface PreferredBrand {
  id?: number;
  // 1-based rank in the waterfall (1 = first choice).
  position: number;
  brand: string;
  // Specific model / product name, e.g. "Ultra Soft Mega Roll".
  product_name?: string;
  // Walmart Business URL for the exact product (where restocking orders are placed).
  link?: string;
  // Units per pack (e.g. a 12-roll pack), so the right pack size is ordered.
  amount?: number;
  notes?: string;
  // True when the tier originates from a backend global default rather than the
  // customer's own selection.
  is_default?: boolean;
}

// Restocking orders are placed on Walmart Business, so product links must point
// there. Mirrors the concierge product-defaults editor.
export const WALMART_BUSINESS_PREFIX = 'https://business.walmart.com/';

export function isWalmartProductLink(link?: string): boolean {
  return !!link && link.trim().toLowerCase().startsWith(WALMART_BUSINESS_PREFIX);
}

// Product pages live under /ip/<slug>/<id>; the slug is what the title fallback
// reads when no scraped product title is available.
const WALMART_BUSINESS_IP_PREFIX = /^https?:\/\/business\.walmart\.com\/ip\//i;

// Human product title derived from a Walmart Business product URL's slug — the
// fallback when we have a link but no scraped og:title.
// ".../ip/Angel-Soft-2-Ply-...-Rolls/484150792?foo=bar" -> "Angel Soft 2 Ply … Rolls".
export function productTitleFromLink(link?: string): string {
  const trimmed = (link || '').trim();
  if (!WALMART_BUSINESS_IP_PREFIX.test(trimmed)) {
    return '';
  }
  const slug = trimmed.replace(WALMART_BUSINESS_IP_PREFIX, '').split('?')[0].split('/')[0];
  try {
    return decodeURIComponent(slug).replace(/-/g, ' ').replace(/\s+/g, ' ').trim();
  } catch {
    return slug.replace(/-/g, ' ').trim();
  }
}

// Forgive a scheme-less paste ("business.walmart.com/..." -> prepend https://).
export function normalizeWalmartLink(link?: string): string {
  const trimmed = (link || '').trim();
  if (!trimmed) {
    return '';
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  if (trimmed.toLowerCase().startsWith('business.walmart.com')) {
    return `https://${trimmed}`;
  }
  return trimmed;
}

// The resolved product name can come back as a bare vendor URL (the concierge
// pastes the Walmart link before a brand name exists). Returns it when it's one,
// so callers can render it as a real link rather than plain text.
export function asHttpUrl(value?: string): string {
  const trimmed = (value || '').trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : '';
}

/**
 * Order quantities are counted in packs, but customers think in units — a "1 pack"
 * of the first-choice toilet paper is 12 rolls. Renders "2 packs (24 items)" when we
 * know the pack size, and falls back to just "2 packs" when we don't (the pack size
 * lives on the preferred-brand tier, which isn't always configured).
 */
export function packLabel(packs: number, unitsPerPack?: number | null): string {
  const packCount = Number(packs) > 0 ? Math.floor(Number(packs)) : 0;
  const packText = `${packCount} ${packCount === 1 ? 'pack' : 'packs'}`;
  const perPack = Number(unitsPerPack);
  if (!(perPack > 0)) {
    return packText;
  }
  const items = packCount * perPack;
  return `${packText} (${items} ${items === 1 ? 'item' : 'items'})`;
}

/**
 * Units per pack for whichever tier the concierge actually resolved to. Restock
 * orders don't carry the pack size, so it's recovered from the room object's
 * preferred-brand waterfall: match on the vendor SKU embedded in the tier's link
 * (the resolved product name is often that same link), else fall back to the first
 * choice, which is what an unresolved order will ultimately be bought as.
 */
export function unitsPerPackFor(
  resolved: { sku?: string; productName?: string } | null | undefined,
  roomObject: { preferred_brands?: PreferredBrand[] } | null | undefined
): number | null {
  const tiers = [...(roomObject?.preferred_brands || [])]
    .sort((a, b) => (a.position || 0) - (b.position || 0));
  if (!tiers.length) {
    return null;
  }
  const sku = (resolved?.sku || '').trim();
  const productName = (resolved?.productName || '').trim();
  const match = tiers.find((tier) => {
    const link = (tier.link || '').trim();
    if (!link) {
      return false;
    }
    return (!!productName && link === productName) || (!!sku && link.includes(sku));
  });
  const amount = Number((match || tiers[0]).amount);
  return amount > 0 ? amount : null;
}

// A tier the user hasn't started filling in. Fully-blank tiers are dropped on
// save rather than flagged as incomplete, so they don't block saving.
export function isPreferredBrandBlank(brand: PreferredBrand): boolean {
  return !brand?.brand?.trim()
    && !brand?.product_name?.trim()
    && !brand?.link?.trim()
    && !brand?.notes?.trim()
    && !(Number(brand?.amount) > 0);
}

/**
 * A tier is only actionable by the concierge with a brand, a pack size (units per
 * pack) and a Walmart Business product link to order from. Shared by the waterfall
 * editor (inline errors) and the item forms (gating save).
 */
export function isPreferredBrandIncomplete(brand: PreferredBrand): boolean {
  return !brand?.brand?.trim()
    || !isWalmartProductLink(brand?.link)
    || !(Number(brand?.amount) > 0);
}
